import { useMemo } from 'react';
import { GenericSetting } from '../../Types/portal';
import { useGetGenericSettingsQuery } from '../useGenericSetting';
import { useSaveGenericSetting } from './useAdminGenericSettings';
import { TimereportSettings } from '../../Types/admin/timereport';

const SETTING_TYPE_TIMEREPORT = 'TIMEREPORT';

const SETTING_KEYS = [
	'EnableFlag',
	'CommentMandatory',
	'HoursPerDay',
	'ShowExternalComment',
	'AllowOvertimeRegistration',
	'VacationDaysDefault',
	'FlexEnabled',
	'DriverLogEnabled',
	'WeekendDaysEnabled',
	'Mode',
	'MonthlySubmitEnabled',
	'ExpenseHideSingleProject',
	'ExpenseHideDateTo',
	'ExpenseHideVat',
];

const getBoolean = (value: string | undefined): boolean => {
	if (!value) return false;
	return value.toLowerCase() === 'true' || value === '1';
};

const getNumber = (value: string | undefined, defaultValue = 0): number => {
	if (!value) return defaultValue;
	const num = parseFloat(value);
	return isNaN(num) ? defaultValue : num;
};

function mapGenericSettingsToTimereportSettings(settings: GenericSetting[]): TimereportSettings {
	return settings.reduce(
		(acc, setting) => {
			switch (setting.Key) {
				case 'CommentMandatory':
					acc.commentMandatory = getBoolean(setting.Value);
					break;
				case 'HoursPerDay':
					acc.hoursPerDay = getNumber(setting.Value, 8);
					break;
				case 'ShowExternalComment':
					acc.showExternalComment = getBoolean(setting.Value);
					break;
				case 'AllowOvertimeRegistration':
					acc.allowOvertimeRegistration = getBoolean(setting.Value);
					break;
				case 'VacationDaysDefault':
					acc.vacationDaysDefault = getNumber(setting.Value, 25);
					break;
				case 'FlexEnabled':
					acc.flexEnabled = getBoolean(setting.Value);
					break;
				case 'DriverLogEnabled':
					acc.driverLogEnabled = getBoolean(setting.Value);
					break;
				case 'WeekendDaysEnabled':
					acc.weekendDaysEnabled = getBoolean(setting.Value);
					break;
				case 'Mode':
					acc.mode = setting.Value === 'week' ? 'week' : 'day';
					break;
				case 'MonthlySubmitEnabled':
					acc.monthlySubmitEnabled = getBoolean(setting.Value);
					break;
				case 'ExpenseHideSingleProject':
					acc.expenseHideSingleProject = getBoolean(setting.Value);
					break;
				case 'ExpenseHideDateTo':
					acc.expenseHideDateTo = getBoolean(setting.Value);
					break;
				case 'ExpenseHideVat':
					acc.expenseHideVat = getBoolean(setting.Value);
					break;
				default:
					break;
			}
			return acc;
		},
		{
			enableFlag: false,
			commentMandatory: false,
			hoursPerDay: 8,
			showExternalComment: false,
			allowOvertimeRegistration: false,
			vacationDaysDefault: 25,
			flexEnabled: false,
			driverLogEnabled: false,
			weekendDaysEnabled: false,
			mode: 'day',
			monthlySubmitEnabled: false,
			expenseHideSingleProject: false,
			expenseHideDateTo: false,
			expenseHideVat: false,
		} as TimereportSettings
	);
}

const CAMEL_TO_SETTING_KEY: Record<keyof TimereportSettings, string> = {
	commentMandatory: 'CommentMandatory',
	hoursPerDay: 'HoursPerDay',
	showExternalComment: 'ShowExternalComment',
	allowOvertimeRegistration: 'AllowOvertimeRegistration',
	vacationDaysDefault: 'VacationDaysDefault',
	flexEnabled: 'FlexEnabled',
	driverLogEnabled: 'DriverLogEnabled',
	weekendDaysEnabled: 'WeekendDaysEnabled',
	mode: 'Mode',
	monthlySubmitEnabled: 'MonthlySubmitEnabled',
	expenseHideSingleProject: 'ExpenseHideSingleProject',
	expenseHideDateTo: 'ExpenseHideDateTo',
	expenseHideVat: 'ExpenseHideVat',
};

export const useTimereportSettings = () => {
	const { saveSetting, statusState } = useSaveGenericSetting();
	const { data: raw, statusState: loadStatusState, refetch } = useGetGenericSettingsQuery(SETTING_TYPE_TIMEREPORT, SETTING_KEYS);
	const settings = useMemo(() => mapGenericSettingsToTimereportSettings(raw ?? []), [raw]);

	function updateSettings(key: keyof TimereportSettings, value: TimereportSettings[typeof key]) {
		saveSetting({
			Key: CAMEL_TO_SETTING_KEY[key],
			Value: value.toString(),
			Type: SETTING_TYPE_TIMEREPORT,
		});
	}

	return { settings, isLoading: raw === undefined, loadStatusState, refetch, updateSettings, updateStatusState: statusState };
};
